import { inject, Injectable } from '@angular/core';
import {
    HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse,
    HttpResponse
} from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take } from 'rxjs/operators';
import { TDSNotificationService } from 'tds-ui/notification';
import { StandardizedError, ApiProblemDetails, ValidationDict } from '../dto/authentication/error.dto';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { StorageHelper } from '../shared/helpers/storage.helper';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    private notification = inject(TDSNotificationService);
    private auth = inject(AuthService);
    private router = inject(Router);
    /** gate refresh to a single in-flight request */
    private refreshing = false;
    private refreshToken$ = new BehaviorSubject<string | null>(null); // emits new access token
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            // 1) Normalize *successful* responses (including 200-with-error-envelope)
            mergeMap(evt => {
                if (!(evt instanceof HttpResponse)) {
                    return of(evt);
                }
                else {
                    const body = evt.body;
                    // detect common API shapes:
                    // { error: {...} }          -> treat as error
                    // { data: { error: {...} } }-> treat as error
                    const apiError = body?.error ?? body?.data?.error ?? body?.detail?.toLowerCase().includes("error");
                    if (apiError) {
                        // Convert to an HttpErrorResponse so the catchError below handles it uniformly
                        return throwError(() => new HttpErrorResponse({
                            status: evt.status,            // will be 200 here
                            statusText: evt.statusText,
                            url: evt.url ?? undefined,
                            error: body                    // keep full body for parsing
                        }));
                    }

                    return of(evt.clone({ body }));
                }
            }),
            // 2) Handle all errors (network, 4xx/5xx, and our synthetic 200-with-error)
            catchError((err: HttpErrorResponse) => {
                if (!(err instanceof HttpErrorResponse)) {
                    this.notification.error('Lỗi', 'Đã xảy ra lỗi không xác định');
                    return throwError(() => err);
                }

                const parsed = this.extractApiError(err);

                switch (err.status) {
                    case 0: // Network error / CORS block / request aborted / offline
                        this.notification.error(parsed.title, 'Không thể kết nối máy chủ. Vui lòng kiểm tra mạng hoặc thử lại sau.');
                        break;
                    case 400: // Bad Request — sai format payload/tham số
                    case 422: // Unprocessable Entity — lỗi validate dữ liệu (server hiểu nhưng không chấp nhận)
                        if (parsed.validation?.length) {
                            const preview = parsed.validation.slice(0, 3).join('\n');
                            this.notification.error(parsed.title, `Dữ liệu không hợp lệ:\n${preview}`);
                            if (parsed.validation.length > 3) {
                                console.warn('All validation errors:', parsed.validation);
                            }
                        } else if (parsed?.error?.Error && parsed?.error?.StatusCode == "InvalidImport") {
                            console.warn(parsed?.error?.StatusCode, parsed.error.Error);
                        }
                        else {
                            this.notification.error(parsed.title, parsed.detail || 'Yêu cầu không hợp lệ');
                        }
                        break;
                    case 401: // Unauthorized — chưa đăng nhập / token hết hạn / token không hợp lệ
                        // The Hangfire cookie bridge is optional and can itself
                        // return 401; refreshing it recursively deadlocks the auth flow.
                        if (req.url.includes('/hangfire-session')) {
                            return throwError(() => err);
                        }
                        return this.tryRefreshToken(req, next, err);
                    case 403: // Forbidden — đã đăng nhập nhưng không có quyền truy cập
                        this.notification.error(parsed.title, parsed.detail || '');
                        break;
                    case 404: // Not Found — không tìm thấy tài nguyên/endpoint
                        this.notification.error('Lỗi', 'Không tìm thấy tài nguyên');
                        break;
                    default: // includes 500 and the synthetic “200 with error”
                        this.notification.error(parsed.title || 'Lỗi hệ thống. Vui lòng thử lại sau.', parsed.detail || 'Có lỗi xảy ra');
                        break;
                }
                return throwError(() => err);
            })
        );
    }
    private extractApiError(err: HttpErrorResponse): StandardizedError {
        console.log("extractApiError");
        const payload = (err.error?.error ?? err.error) as ApiProblemDetails | string | null;
        let title = '';
        let detail: string | null | undefined = undefined;
        let validation: string[] | undefined;

        if (payload && typeof payload === 'object') {
            title = (payload['title'] ?? err.statusText ?? '').toString();
            detail = payload['details'] ?? payload['message'] ?? null;

            const v = payload.validationErrors;
            if (Array.isArray(v)) {
                validation = v.map((it: any) => {
                    const field = it.field || it.name || (Array.isArray(it.memberNames) ? it.memberNames.join(', ') : '');
                    return field ? `${field}: ${it.message}` : `${it.message}`;
                });
            } else if (v && typeof v === 'object') {
                // Dictionary form: { field: ["msg1", "msg2"], ... }
                validation = Object.entries(v as ValidationDict).flatMap(([field, messages]) =>
                    Array.isArray(messages) ? messages.map(m => `${field}: ${m}`) : [`${field}: ${String(messages)}`]
                );
            }
        } else if (typeof payload === 'string') {
            // e.g., server returned text/html or plain string
            detail = payload;
        }

        // Sensible fallbacks
        if (!title || title.trim() === '') {
            const msg = err.message?.trim() ?? '';

            if (err.status === 0) {
                title = 'Không thể kết nối máy chủ';
            } else if (msg.length > 0 && msg.length < 25) {
                title = msg;
            } else {
                if (msg.length > 0)
                {
                    console.warn('Error message:', msg);
                }
                title = 'Đã xảy ra lỗi khi xử lý yêu cầu';
            }
        }

        return {
            status: err.status,
            title,
            detail,
            validation,
            raw: err,
            error: err.error
        };
    }
    private addAuth(req: HttpRequest<any>, accessToken?: string) {
        const token = accessToken ?? StorageHelper.getToken()?.accessToken;
        return token
            ? req.clone({ setHeaders: { Authorization: `Bearer ${token}`, Token: token } })
            : req;
    }

    private forceLogout() {
        try { StorageHelper.removeToken(); } catch { }
        this.notification.error('Phiên đăng nhập đã hết hạn', 'Vui lòng đăng nhập lại');
        this.router.navigateByUrl('/auth/login');
    }

    private tryRefreshToken(req: HttpRequest<any>, next: HttpHandler, err: HttpErrorResponse): Observable<HttpEvent<any>> {
        // avoid loops (don’t refresh calls to the refresh endpoint itself)
        if (req.headers.get('X-Retry-After-Refresh') === '1' || req.url.includes('/sign-in/refresh-token')) {
            this.forceLogout();
            return throwError(() => err);
        }
        const refreshToken = StorageHelper.getToken()?.refreshToken;
        if (!refreshToken) {
            this.forceLogout();
            return throwError(() => err);
        }
        // If not currently refreshing: start refresh, then retry original
        if (!this.refreshing) {
            this.refreshing = true;
            this.refreshToken$.next(null); // signal “refresh started”
            return this.auth.refreshToken(refreshToken).pipe(
                switchMap(res => {
                    if (res?.accessToken) {
                        // save new tokens
                        StorageHelper.setToken(res);
                        const newAccess = res.accessToken ?? '';

                        // wake waiting requests
                        this.refreshing = false;
                        this.refreshToken$.next(newAccess);

                        // retry original once, tagging to avoid loops
                        const retried = this.addAuth(req, newAccess).clone({
                            setHeaders: { 'X-Retry-After-Refresh': '1' }
                        });
                        return next.handle(retried);
                    }
                    // refresh failed: logout and propagate error
                    this.forceLogout();
                    return throwError(() => err);
                }),
                catchError(e => {
                    this.refreshing = false;
                    this.forceLogout();
                    return throwError(() => e);
                })
            );
        }
        // Already refreshing: wait for the new token, then retry once
        return this.refreshToken$.pipe(
            filter(t => !!t),
            take(1),
            switchMap(token => {
                const retried = this.addAuth(req, token as string).clone({
                    setHeaders: { 'X-Retry-After-Refresh': '1' }
                });
                return next.handle(retried);
            })
        );
    }
}
